import _jsx from "@babel/runtime/helpers/jsx";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FormattedMessage, injectIntl } from 'react-intl';
import AppContext from '../../context';
import VISMA_DOC_STATUSES, { VISMA_INVITATION_STATUSES } from '../../constants/vismaDocStatuses';
import intlPropTypes from '../../constants/intlPropTypes';
import VismaSign from '../statelessComponents/Icons/VismaSign';
import ConfirmDeleteDialog from '../statelessComponents/ConfirmDeleteDialog';
import messages from './messages';
var REMINDABLE_STATUSES = [VISMA_INVITATION_STATUSES.waitingForSend, VISMA_INVITATION_STATUSES.sending, VISMA_INVITATION_STATUSES["new"], VISMA_INVITATION_STATUSES.opened];
var getSigners = function getSigners(vismaSign) {
  if (vismaSign !== null && vismaSign !== void 0 && vismaSign.signerEmail) {
    var _vismaSign$invitation, _vismaSign$invitation2;
    return [{
      email: vismaSign.signerEmail,
      invitationUuid: (_vismaSign$invitation = vismaSign.invitation) === null || _vismaSign$invitation === void 0 ? void 0 : _vismaSign$invitation.uuid,
      status: (_vismaSign$invitation2 = vismaSign.invitation) === null || _vismaSign$invitation2 === void 0 ? void 0 : _vismaSign$invitation2.status
    }];
  }
  return ((vismaSign === null || vismaSign === void 0 ? void 0 : vismaSign.signerEmails) || []).map(function (email, index) {
    var _vismaSign$invitation3, _vismaSign$invitation4, _vismaSign$invitation5, _vismaSign$invitation6;
    return {
      email: email,
      invitationUuid: (_vismaSign$invitation3 = vismaSign.invitations) === null || _vismaSign$invitation3 === void 0 ? void 0 : (_vismaSign$invitation4 = _vismaSign$invitation3[index]) === null || _vismaSign$invitation4 === void 0 ? void 0 : _vismaSign$invitation4.uuid,
      status: (_vismaSign$invitation5 = vismaSign.invitations) === null || _vismaSign$invitation5 === void 0 ? void 0 : (_vismaSign$invitation6 = _vismaSign$invitation5[index]) === null || _vismaSign$invitation6 === void 0 ? void 0 : _vismaSign$invitation6.status
    };
  });
};
var ResendVismaSignInvitation = function ResendVismaSignInvitation(_ref) {
  var _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$iconClassName = _ref.iconClassName,
    iconClassName = _ref$iconClassName === void 0 ? '' : _ref$iconClassName,
    formatMessage = _ref.intl.formatMessage,
    onSuccess = _ref.onSuccess,
    submitRoute = _ref.submitRoute,
    _ref$vismaSign = _ref.vismaSign,
    vismaSign = _ref$vismaSign === void 0 ? null : _ref$vismaSign;
  var _useContext = useContext(AppContext),
    fetch = _useContext.fetch;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDialogOpen = _useState2[0],
    setIsDialogOpen = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSubmitting = _useState4[0],
    setIsSubmitting = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    selectedUuids = _useState6[0],
    setSelectedUuids = _useState6[1];
  var _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    submitError = _useState8[0],
    setSubmitError = _useState8[1];
  var signers = useMemo(function () {
    return getSigners(vismaSign);
  }, [vismaSign]);
  var remindableSigners = signers.filter(function (_ref2) {
    var invitationUuid = _ref2.invitationUuid,
      status = _ref2.status;
    return invitationUuid && REMINDABLE_STATUSES.includes(status);
  });
  if ((vismaSign === null || vismaSign === void 0 ? void 0 : vismaSign.status) !== VISMA_DOC_STATUSES.pending || !remindableSigners.length) {
    return null;
  }
  var closeDialog = function closeDialog() {
    if (isSubmitting) return;
    setIsDialogOpen(false);
    setSelectedUuids([]);
    setSubmitError('');
  };
  var toggleSigner = function toggleSigner(invitationUuid) {
    setSelectedUuids(function (current) {
      return current.includes(invitationUuid) ? current.filter(function (uuid) {
        return uuid !== invitationUuid;
      }) : [].concat(_toConsumableArray(current), [invitationUuid]);
    });
  };
  var getStatusLabel = function getStatusLabel(status) {
    if (status === VISMA_INVITATION_STATUSES.signed) {
      return formatMessage(messages.signedStatus);
    }
    if (status === VISMA_INVITATION_STATUSES.cancelled) {
      return formatMessage(messages.cancelledStatus);
    }
    return formatMessage(messages.unavailableStatus);
  };
  var submit = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var response, message, responseBody;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            setIsSubmitting(true);
            setSubmitError('');
            _context.prev = 2;
            _context.next = 5;
            return fetch(submitRoute, {
              method: 'POST',
              body: JSON.stringify({
                invitationUuids: selectedUuids
              })
            });
          case 5:
            response = _context.sent;
            if (response.ok) {
              _context.next = 18;
              break;
            }
            message = formatMessage(messages.requestFailed);
            _context.prev = 8;
            _context.next = 11;
            return response.json();
          case 11:
            responseBody = _context.sent;
            message = responseBody.message || message;
            _context.next = 17;
            break;
          case 15:
            _context.prev = 15;
            _context.t0 = _context["catch"](8);
          case 17:
            throw new Error(message);
          case 18:
            setIsDialogOpen(false);
            setSelectedUuids([]);
            onSuccess();
            _context.next = 26;
            break;
          case 23:
            _context.prev = 23;
            _context.t1 = _context["catch"](2);
            setSubmitError(_context.t1.message || formatMessage(messages.requestFailed));
          case 26:
            _context.prev = 26;
            setIsSubmitting(false);
            return _context.finish(26);
          case 29:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[2, 23, 26, 29], [8, 15]]);
    }));
    return function submit() {
      return _ref3.apply(this, arguments);
    };
  }();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(Tooltip, {
    title: formatMessage(messages.buttonLabel)
  }, void 0, /*#__PURE__*/_jsx(Button, {
    "data-test-id": "resend-visma-sign-invitation",
    variant: "outlined",
    color: "primary",
    className: className,
    onClick: function onClick() {
      return setIsDialogOpen(true);
    }
  }, void 0, /*#__PURE__*/_jsx(VismaSign, {
    className: iconClassName
  }), /*#__PURE__*/React.createElement(FormattedMessage, messages.buttonLabel))), /*#__PURE__*/_jsx(ConfirmDeleteDialog, {
    isShown: isDialogOpen,
    onClose: closeDialog,
    submit: submit,
    submitDisabled: !selectedUuids.length,
    isDeleting: isSubmitting,
    deletingError: submitError,
    onCloseSnackbar: function onCloseSnackbar() {
      return setSubmitError('');
    },
    deleteBtnId: "confirm-resend-visma-sign-invitation",
    submitColor: "primary",
    deleteBtnLabel: /*#__PURE__*/React.createElement(FormattedMessage, messages.resendLabel),
    title: /*#__PURE__*/React.createElement(FormattedMessage, messages.dialogTitle),
    description: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx(Typography, {
      component: "p"
    }, void 0, /*#__PURE__*/React.createElement(FormattedMessage, messages.dialogDescription)), /*#__PURE__*/_jsx(FormGroup, {}, void 0, signers.map(function (_ref4) {
      var email = _ref4.email,
        invitationUuid = _ref4.invitationUuid,
        status = _ref4.status;
      var isRemindable = invitationUuid && REMINDABLE_STATUSES.includes(status);
      var label = isRemindable ? email : "".concat(email, " (").concat(getStatusLabel(status), ")");
      return /*#__PURE__*/_jsx(FormControlLabel, {
        control: /*#__PURE__*/_jsx(Checkbox, {
          "data-test-id": "resend-visma-sign-invitation-".concat(invitationUuid),
          checked: selectedUuids.includes(invitationUuid),
          onChange: function onChange() {
            return toggleSigner(invitationUuid);
          },
          disabled: !isRemindable || isSubmitting
        }),
        label: label
      }, invitationUuid || email);
    })))
  }));
};
export default injectIntl(ResendVismaSignInvitation);